import {
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
  type DocumentSnapshot,
  type Firestore,
} from 'firebase/firestore';
import type { UserProfile, UserRepository, UserStatus } from '@nx/domain';
import type { AppRole, AuthProvider } from '@nx/domain';

export const USERS_COLLECTION = 'users';

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value && 'toDate' in value) {
    const result = (value as { toDate: () => Date }).toDate();
    return result instanceof Date ? result : null;
  }
  return null;
}

export function mapUserSnapshot(snapshot: DocumentSnapshot): UserProfile | null {
  if (!snapshot.exists()) return null;
  const data = snapshot.data() ?? {};
  return {
    uid: snapshot.id,
    email: (data.email as string | undefined) ?? null,
    displayName: (data.displayName as string | undefined) ?? null,
    photoURL: (data.photoURL as string | undefined) ?? null,
    phone: (data.phone as string | undefined) ?? null,
    provider: (data.provider as AuthProvider | undefined) ?? 'password',
    appRole: (data.appRole as AppRole | undefined) ?? 'user',
    companyCode: (data.companyCode as string | undefined) ?? null,
    companyName: (data.companyName as string | undefined) ?? null,
    userStatus: (data.userStatus as UserStatus | undefined) ?? 'enabled',
    mustChangePassword: Boolean(data.mustChangePassword),
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export class FirebaseUserRepository implements UserRepository {
  constructor(private readonly db: Firestore) {}

  async findById(uid: string): Promise<UserProfile | null> {
    const ref = doc(this.db, USERS_COLLECTION, uid);
    const snap = await getDoc(ref);
    return mapUserSnapshot(snap);
  }

  async setMustChangePassword(uid: string, mustChange: boolean): Promise<void> {
    const ref = doc(this.db, USERS_COLLECTION, uid);
    await updateDoc(ref, {
      mustChangePassword: mustChange,
      passwordChangedAt: mustChange ? null : serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
}
